function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useMemo, useState } from 'react';
import { CustomModal, Button, TableList, PageHeader, Spinner, Typography, PageEmptyState, PageErrorState, FilterPickerGroup, Searchbox, MultiSelectFilterPicker } from 'seagull';
import keyBy from 'lodash/keyBy';
import styles from "./MapContactsModal.module.scss";
import { FooterState, ContentState, columnDefs, headerItems, getModalState, getNumMappedContacts, applyFilters, getRelevantSortedContacts } from "./MapContactsModal.utils";
import { useStore, useStoreActions } from "../../../store/store-context";
import MapContactsRow from "./MapContactsRow";
import { modalSize } from "../../../infra/modals";
import NoDataIcon from "../../assets/NoDataIcon";
import { CreateProposalButton } from "../../../components/CreateProposalButton";
import { handleRequest } from "../../../infra/utils";
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
// Adapter for react-window
function ContactsRowComponent(props) {
  const {
    data,
    index,
    style
  } = props;
  const integratedId = data.anchorContacts[index].integratedId();
  const integratedQboCustomer = integratedId ? data.qboCustomersDictionary[integratedId] : undefined;
  return /*#__PURE__*/React.createElement("div", {
    style: style
  }, /*#__PURE__*/React.createElement(MapContactsRow, {
    anchorContact: data.anchorContacts[index],
    qboCustomers: data.qboCustomers,
    integratedQboCustomer: integratedQboCustomer,
    changeContactLink: data.changeContactLink,
    createNewCustomer: data.createNewCustomer,
    testid: 'map-contacts-row',
    key: data.anchorContacts[index].id(),
    onInputFocus: data.onInputFocus,
    onInputBlur: data.onInputBlur
  }));
}
export function MapContactsModal(props) {
  const {
    dismissModal,
    onChange,
    contactIds
  } = props;
  const contactsLoadableState = useStore(state => state.contacts);
  const unsortedAnchorContacts = contactsLoadableState.data;
  const qboCustomersLoadableState = useStore(state => state.qboCustomers);
  const qboCustomers = qboCustomersLoadableState.data;
  const {
    api,
    fetchContacts,
    fetchQboCustomers
  } = useStoreActions();
  const [anchorContacts, setAnchorContacts] = useState([]);
  const inputDebounceTime = useStore(state => state.appConfig.inputDebounceTime);
  const [registerSearchBoxHotkey, setRegisterSearchBoxHotkey] = useState(true);
  const [search, setSearch] = useState('');
  const [contactMappingStatusFilter, setContactMappingStatusFilter] = useState([]);
  const numOfMappedContacts = useMemo(() => getNumMappedContacts(anchorContacts), [anchorContacts]);
  const modalState = useMemo(() => getModalState(contactsLoadableState, qboCustomersLoadableState), [contactsLoadableState, qboCustomersLoadableState]);
  const qboCustomersDictionary = useMemo(() => keyBy(qboCustomers, 'id'), [qboCustomers]);
  const {
    filteredAnchorContacts,
    contactMappingStatusFilterOptions
  } = useMemo(() => applyFilters(anchorContacts, search, contactMappingStatusFilter), [anchorContacts, search, contactMappingStatusFilter]);
  useEffect(() => {
    const filteredAndSorted = getRelevantSortedContacts(unsortedAnchorContacts, contactIds || []);
    setAnchorContacts(filteredAndSorted);
  }, [contactIds, unsortedAnchorContacts]);
  useEffect(() => {
    refreshData();
  }, []);
  async function refreshData() {
    fetchContacts();
    fetchQboCustomers();
  }
  async function changeContactLink(anchorContact, qboCustomerId) {
    const link = await handleRequest(api.contact.qboLink(anchorContact.id(), {
      integratedId: qboCustomerId,
      version: anchorContact.version()
    }));
    if (!link.success) {
      throw link.error;
    }
    onChange && onChange();
    const updatedContact = await handleRequest(api.contact.getById(anchorContact.id()));
    if (!updatedContact.success) {
      throw updatedContact.error;
    }
    updateContact(updatedContact.data);
  }
  async function createNewCustomer(anchorContact) {
    await api.contact.qboCreate(anchorContact.id());
    onChange && onChange();
    const updatedContact = await api.contact.getById(anchorContact.id());
    updateContact(updatedContact);
    await fetchQboCustomers();
  }
  function updateContact(contact) {
    const newAnchorContacts = anchorContacts.map(c => c.id() == contact.id() ? contact : c);
    setAnchorContacts(newAnchorContacts);
  }
  function getFooter() {
    switch (modalState.footer) {
      case FooterState.Valid:
        return /*#__PURE__*/React.createElement("div", {
          className: styles.footer
        }, anchorContacts && /*#__PURE__*/React.createElement("div", {
          "data-testid": "total-map-status"
        }, /*#__PURE__*/React.createElement(Typography, null, "".concat(numOfMappedContacts, " of ").concat(anchorContacts.length, " contacts mapped"))), /*#__PURE__*/React.createElement(Button, {
          onClick: dismissModal,
          "data-testid": "qbo-map-contacts-close"
        }, "Close"));
      case FooterState.Invalid:
        return null;
    }
  }
  function getContent() {
    switch (modalState.content) {
      case ContentState.Valid:
        return /*#__PURE__*/React.createElement("div", {
          className: styles.content
        }, /*#__PURE__*/React.createElement("div", {
          style: {
            marginBlockEnd: '24px'
          }
        }, /*#__PURE__*/React.createElement(FilterPickerGroup, {
          flexWrap: true,
          "data-testid": "map-contacts-modal-filters"
        }, /*#__PURE__*/React.createElement(Searchbox, {
          registerHotkey: registerSearchBoxHotkey,
          value: search,
          "data-testid": "map-contacts-modal-search",
          debounceTime: inputDebounceTime !== null && inputDebounceTime !== void 0 ? inputDebounceTime : 500,
          onChange: event => setSearch(event.target.value),
          onClear: () => setSearch('')
        }), /*#__PURE__*/React.createElement(MultiSelectFilterPicker, {
          "data-testid": "qbo-mapping-status-filter-picker",
          value: contactMappingStatusFilter,
          options: contactMappingStatusFilterOptions,
          label: "Is mapped",
          onChange: setContactMappingStatusFilter
        }))), /*#__PURE__*/React.createElement(TableList.Header, {
          items: headerItems
        }), filteredAnchorContacts && /*#__PURE__*/React.createElement("div", {
          className: styles.tableContainer
        }, /*#__PURE__*/React.createElement(AutoSizer, null, _ref => {
          let {
            height,
            width
          } = _ref;
          return /*#__PURE__*/React.createElement(FixedSizeList, {
            itemData: {
              anchorContacts: filteredAnchorContacts,
              qboCustomers,
              qboCustomersDictionary,
              createNewCustomer,
              changeContactLink,
              onInputFocus: () => setRegisterSearchBoxHotkey(false),
              onInputBlur: () => setRegisterSearchBoxHotkey(true)
            },
            itemCount: filteredAnchorContacts.length,
            itemSize: 64,
            height: height,
            width: width,
            overscanCount: 50
          }, ContactsRowComponent);
        })));
      case ContentState.Loading:
        return /*#__PURE__*/React.createElement("div", {
          className: styles.loading
        }, /*#__PURE__*/React.createElement(Spinner, {
          size: 40,
          "data-testid": "map-contacts-loading"
        }));
      case ContentState.Empty:
        return /*#__PURE__*/React.createElement(PageEmptyState, {
          "data-testid": "map-contacts-empty-state",
          illustration: /*#__PURE__*/React.createElement(NoDataIcon, null),
          message: "In order to map with your QuickBooks online account, start by creating an agreement",
          actions: /*#__PURE__*/React.createElement(CreateProposalButton, {
            text: "Create a new agreement",
            hidePrefix: true,
            "data-testid": "map-contacts-new-btn"
          })
        });
      case ContentState.Error:
        return /*#__PURE__*/React.createElement(PageErrorState, {
          "data-testid": "map-contacts-error-state",
          message: "Failed to retrieve the relevant data please try again later",
          onRefresh: refreshData,
          skin: "secondary"
        });
    }
  }
  return /*#__PURE__*/React.createElement(CustomModal, _extends({
    "data-testid": "qbo-map-contacts-modal",
    footer: getFooter()
  }, modalSize.large, {
    onClose: dismissModal
  }), /*#__PURE__*/React.createElement(Typography, {
    className: styles.modalContainer
  }, /*#__PURE__*/React.createElement(PageHeader, {
    title: "Map contacts to QuickBooks Online",
    subheader: "Map Anchor contacts to QuickBooks Online customers."
  }), /*#__PURE__*/React.createElement(TableList.Provider, {
    columnDefs: columnDefs
  }, getContent())));
}